import Vue from "vue";
import { mapGetters } from "vuex";

import moment from "moment";
import { oc } from "ts-optchain";

import { CreditsService, DealerUtil, ErrorLogService, GridUtil } from "@/services/";

import {
    AvailableDealerCodeSelector,
    CurrencyText,
    ListLayout,
    MonthSelector,
    OrdersMenu,
    OrdersSearchWidgetForm,
    LoadingSpinner,

} from "@/components";
import DnTable from "@/components/Elements/DnTable/DnTable.vue";
import { formatDateTime, paginationMixin, stringHelper } from "@/components/mixins";

import { ICreditMemoRowItem, IDealerCodeItem, IDealerInfo, IUserInfo } from "@/types";

export default Vue.extend({
    name: "Credits",
    components: {
        ListLayout,
        OrdersMenu,
        OrdersSearchWidgetForm,
        CurrencyText,
        AvailableDealerCodeSelector,
        MonthSelector,
        LoadingSpinner,
        DnTable,
    },
    mixins: [formatDateTime, paginationMixin, stringHelper],
    
    created() {
        this.allColumns = GridUtil.localizeColumnLabels(this.allColumns, this.getContentItem);

        const dealerCodes: IDealerInfo[] = this.availableDealerCodes || [];
        const user: IUserInfo = this.user;
        const hasAccessToAllBranches: boolean = this.hasAccessToAllBranches;

        this.allPrimaryDealerCodes = dealerCodes.map((dealer) => dealer.PrimaryDealerCode);

        this.currentDealerSelection = DealerUtil.determineCurrentDealerSelection(
            dealerCodes,
            user,
            hasAccessToAllBranches
        );
        this.currentMonthSelection = moment().format("MMMM YYYY");

        if (this.getRecentComponentName === "Credits") {
            const componentData = this.getRecentComponentData || {};

            this.currentMonthSelection = componentData.currentMonthSelection;
            this.stagedDealerSelection = componentData.stagedDealerSelection;

            const primaryDealerCode = oc(componentData.stagedDealerSelection).PrimaryDealerCode("");

            if (primaryDealerCode) {
                this.currentDealerSelection = dealerCodes.find(
                    (dealer) => dealer && dealer.PrimaryDealerCode === primaryDealerCode
                );
            }
        }
    },  

    computed: {
        ...mapGetters([
            "user",
            "getContentItem",
            "hasPermission",
            "canSeeAnyPrice",
            "availableDealerCodes",
            "activeAccountId",
            "getRecentComponentName",
            "getRecentComponentData",
            "IsMcfe"
        ]),
        canPrintInvoices() {
            return !this.hasPermission("HideInvoicePricing") && !this.IsMcfe;
        },
        canViewPrice() {
            return this.canSeeAnyPrice;
        },
        columns() {
            if (this.canViewPrice) {
                return this.allColumns;
            }
            return this.allColumns.filter(
                (column: { key: string }) => column.key !== "freight" && column.key !== "total"
            );
        },
        dealerFilter() {
            return DealerUtil.dealerFilter(this.currentDealerSelection, this.allPrimaryDealerCodes);
        },
        exportEnabled() {
            return oc(this.selectedRows as []).length(0) > 0;
        },
        hasAccessToAllBranches() {
            return DealerUtil.hasAccessToAllBranches(this.availableDealerCodes, this.user);
        },
        message() {
            const displayWithSubDealers = oc(this.currentDealerSelection as IDealerCodeItem).DisplayWithSubDealers("");

            return this.format(
                this.getContentItem("selectedMessage"),
                this.getContentItem("credits"),
                displayWithSubDealers,
                this.currentMonthSelection
            );
        },
        monthFilter() {
            return moment(this.currentMonthSelection, "MMMM YYYY");
        },
    },

    data: () => ({
        allColumns: [
            { key: "selection", sortable: false },
            { key: "invoiceDate", localizationKey: "date", sortable: true },
            { key: "invoiceNumber", localizationKey: "creditInvoiceNumber", sortable: true },
            { key: "referenceNumber", localizationKey: "referenceNumber", sortable: true },
            { key: "purchaseOrderNumber", localizationKey: "purchaseOrderNumber", sortable: true },
            { key: "freight", localizationKey: "freight", sortable: true },
            { key: "total", localizationKey: "total", sortable: true },
        ],
        isLoadingCredits: false,
        allPrimaryDealerCodes: null,
        currentDealerSelection: null,
        currentMonthSelection: null,
        exportAction: CreditsService.getExportAction(),
        selectedRows: [],
        stagedDealerSelection: null,
        stagedMonthSelection: null,
        textFilter: null,
        credits: []
    }),

    methods: {
        async loadCredits() {
            try {
                this.isLoadingCredits = true
                this.paginationReset([]);

                const dateFormat = "MM/DD/YYYY";

                var dealerCodesToSearch = this.dealerFilter;
                if(dealerCodesToSearch.length > 100)
                {
                    dealerCodesToSearch = [this.user.DealerCode];
                }

                const params = {
                    accountId: this.activeAccountId,
                    userId: this.user.UserId,
                    startDate: this.monthFilter.startOf("month").format(dateFormat),
                    endDate: this.monthFilter.endOf("month").format(dateFormat),
                    dealerCodes: dealerCodesToSearch,
                };

                const result = await CreditsService.getCredits(params);
                this.credits = result;
                this.pagination.totalRows = result.length;

                return result;
            } catch (err) {
                ErrorLogService.LogException(err);
                return [];
            } finally{
                this.isLoadingCredits = false
            }
        },

        filterButtonClick() {
            if (this.stagedDealerSelection) {
                this.currentDealerSelection = this.stagedDealerSelection;
            }
            if (this.stagedMonthSelection) {
                this.currentMonthSelection = this.stagedMonthSelection;
            }

           this.loadCredits();
        },

        filterGrid(row: any) {
            let filterCheck = GridUtil.filterGrid(row, this.textFilter);
            if( !filterCheck){
                for (let rowCellKey in row) { 
                    let rowCellValue = row[rowCellKey] 

                    //Date match check
                    let rowCellValueAsDate = this.formatDateTime(rowCellValue);
                    if(rowCellValueAsDate !== ""){  
                        let rowCellValueAsDateWithoutSpecialChars = rowCellValueAsDate
                        let textFilterWithoutSpecialChars = this.textFilter;
                        let specialCharsRegex = /\/|\.|\\|\-/gi  
                        rowCellValueAsDateWithoutSpecialChars = rowCellValueAsDateWithoutSpecialChars.replace(specialCharsRegex ,"");
                        textFilterWithoutSpecialChars = textFilterWithoutSpecialChars.replace(specialCharsRegex ,"");
                        if(rowCellValueAsDateWithoutSpecialChars.includes(textFilterWithoutSpecialChars)){
                            return true;
                        }
                    }
                }
            }

            return filterCheck;
        },

        selectAllRows() {
            let allRows = this.$refs.creditsTableRef.$el.getElementsByTagName("tbody")[0].getElementsByTagName("tr");
            
            for(let i = 0; i < allRows.length; i++) {
              this.propagateSelection(i);
            }
          },

        propagateSelection(rowIndex: number) {
            this.selectedRows.push(rowIndex);
        },

        rowSelected(items: ICreditMemoRowItem[]) {
            this.selectedRows = items;
        },

        setRecentComponentData() {
            this.$store.commit("SET_RECENT_COMPONENT", {
                name: "Credits",
                data: {
                    currentMonthSelection: this.currentMonthSelection,
                    stagedDealerSelection: this.stagedDealerSelection,
                },
            });
        },

        setSelectedDealerCode(selectedDealer: any) {
            this.stagedDealerSelection = selectedDealer;
        },

        setSelectedMonth(selectedMonth: any) {
            this.stagedMonthSelection = selectedMonth;
        },
    },

    watch: {
        currentMonthSelection() {
            this.setRecentComponentData();
        },
        stagedDealerSelection() {
            this.setRecentComponentData();
        },
    },
});
